import React from 'react';

type ModuleErrorBoundaryProps = {
  moduleName?: string;
  children: React.ReactNode;
};

type ModuleErrorBoundaryState = {
  error: Error | null;
};

/** Catches render errors in lazy-loaded modules so the page is not blank. */
export class ModuleErrorBoundary extends React.Component<
  ModuleErrorBoundaryProps,
  ModuleErrorBoundaryState
> {
  state: ModuleErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ModuleErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[module render error]', error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="p-6">
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 space-y-2">
            <p className="font-semibold">
              {this.props.moduleName ? `${this.props.moduleName} failed to load` : 'Module failed to load'}
            </p>
            <p>{this.state.error.message || 'An unexpected error occurred while rendering this screen.'}</p>
            <button
              type="button"
              className="mt-2 px-3 py-1.5 border border-red-300 rounded-md bg-white text-red-800"
              onClick={() => this.setState({ error: null })}
            >
              Try again
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
